import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import {
  Card,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { Zap, Users, Settings } from "lucide-react";

export function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated, loading } = useAuth();
  const { currentTheme } = useTheme();

  useEffect(() => {
    if (!loading) {
      if (isAuthenticated) {
        navigate("/");
      }
    }
  }, [isAuthenticated, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-4"
            style={{ borderColor: currentTheme.colors.primary }}
          ></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Logo size={32} showText={true} />
          <Button
            onClick={() => {
              navigate("/login");
            }}
            style={{ backgroundColor: currentTheme.colors.primary }}
          >
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-4">Welcome to SnapShift Admin Panel</h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Manage your SnapShift platform with powerful tools and insights. Monitor
            users, content, notifications, and more from a centralized dashboard.
          </p>
          <Button
            size="lg"
            onClick={() => {
              navigate("/login");
            }}
            style={{ backgroundColor: currentTheme.colors.primary }}
          >
            Get Started
          </Button>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-muted/50">
        <div className="container mx-auto px-4">
          <h3 className="text-2xl font-bold text-center mb-12">
            Powerful Admin Features
          </h3>
          <div className="grid md:grid-cols-3 gap-8">
            <Card>
              <CardHeader>
                <Users
                  className="w-8 h-8 mb-2"
                  style={{ color: currentTheme.colors.primary }}
                />
                <CardTitle>User Management</CardTitle>
                <CardDescription>
                  Manage users, view profiles, and monitor user activity across the
                  platform.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <Zap
                  className="w-8 h-8 mb-2"
                  style={{ color: currentTheme.colors.primary }}
                />
                <CardTitle>Content Control</CardTitle>
                <CardDescription>
                  Manage app content, FAQs, and ensure quality control across all
                  materials.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card>
              <CardHeader>
                <Settings
                  className="w-8 h-8 mb-2"
                  style={{ color: currentTheme.colors.primary }}
                />
                <CardTitle>System Settings</CardTitle>
                <CardDescription>
                  Configure platform settings, manage notifications, and customize the
                  experience.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>&copy; 2024 SnapShift. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
